import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { DirectionFindDto, type DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { UnitFindDto, type UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import { ChecklistFindDto, type ChecklistFindInterface } from '@zaoblako/shared/modules/project/dto/checklist/checklist-find.dto';
import { DeadlineDelayRequestFindDto, type DeadlineDelayRequestFindInterface } from '@zaoblako/shared/modules/project/dto/deadline-delay-request/deadline-delay-request-find.dto';
import { MasterMilestoneFindDto, type MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import { ProjectWorkflowPermissionsDto, type ProjectWorkflowPermissionsInterface } from '@zaoblako/shared/modules/project/dto/project/project-workflow-permissions.dto';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import { StatusFindDto, type StatusFindInterface } from '@zaoblako/shared/modules/project/dto/status/status-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { SubprojectDefaultsFindDto, type SubprojectDefaultsFindInterface } from '@zaoblako/shared/modules/project/dto/subproject-defaults/subproject-defaults-find.dto';
import { ClientFindDto, type ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { NotificationDtoKeysEnum } from '@zaoblako/shared/modules/notification/enums/notification-dto-keys.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import type { ProjectTemplateFindInterface } from '@zaoblako/shared/modules/project/dto/template/project-template-find.dto';

import * as yup from 'yup';

export interface ProjectFindInterface {
    /** Unique identifier. Cannot be changed. */
  id: number;
    /** Project name */
  name: string;
    /** Project description */
  description: string;
    /** Project creation date and time */
  created: Date;
    /** Project last update date and time */
  updated: Date;
    /** Is project deleted */
  deleted: boolean;
    /** Project deadline date and time */
  deadline?: Date;
    /** Time spent on the project **/
  factMin: number;
    /** Time planned to spent on the project */
  plannedMin: number;
    /** Is project not closed after deadline */
  overdue: boolean;
  priority?: HelpdeskTaskPriorityEnum;
    /** Project creator user */
  creator?: UserFindInterface;
    /** User which responsible for this project (main executor) */
  responsible?: UserFindInterface;
    /** Users which adds this project to favorites */
  favorites?: UserFindInterface[];
    /** Project user-auditors */
  userAuditors?: UserFindInterface[];
    /** Project group-auditors */
  userGroupAuditors?: UserGroupFindInterface[];
    /** Parent project if exists */
  parent?: ProjectFindInterface;
    /** Child projects */
  children?: ProjectFindInterface[];
    /** Attached files if exist */
  files?: FileFindInterface[];
    /** Number of attached files */
  filesCount?: number;
  status: StatusFindInterface;
    /** Date and of project finish. If project is not finished, then value is `null`. */
  finishedAt: Date;
  isFavorite?: boolean;
  hasAccess?: boolean;
  hasDescendants?: boolean;
  checklists: ChecklistFindInterface[];
  masterMilestone?: MasterMilestoneFindInterface;
  nextTodo?: ChecklistFindInterface;
  reminder?: ReminderFindInterface;
  deadlineDelayRequest: DeadlineDelayRequestFindInterface;
  workflowPermissions?: ProjectWorkflowPermissionsInterface;
  deals?: DealFindInterface[];
  client?: ClientFindInterface;
  direction?: DirectionFindInterface;
  unit?: UnitFindInterface;
  filial?: FilialFindInterface;
  helpdeskTasks?: HelpdeskTaskFindInterface[];
  subprojectDefaults?: SubprojectDefaultsFindInterface;
  thread?: ThreadFindInterface;
  template?: ProjectTemplateFindInterface;
}

export const ProjectFindDto = SerdeDtoClass.create<ProjectFindInterface>(ProjectDtoKeysEnum.PROJECT_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  name: {
    sortable: true,
    label: 'Имя',
    serializer: yup.string(),
  },
  description: {
    label: 'Описание',
    serializer: yup.string(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    sortable: true,
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Удален',
    serializer: yup.boolean(),
  },
  deadline: {
    sortable: true,
    optional: true,
    label: 'Дедлайн',
    serializer: yup.date(),
  },
  factMin: {
    label: 'Минимальный факт',
    serializer: yup.number(),
  },
  plannedMin: {
    label: 'Запланированный минимум',
    serializer: yup.number(),
  },
  overdue: {
    label: 'Просрочен',
    serializer: yup.boolean(),
  },
  priority: {
    sortable: true,
    optional: true,
    label: 'Приоритет',
    serializer: yup
      .string()
      .oneOf(Object.values(HelpdeskTaskPriorityEnum)),
  },
  creator: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  responsible: {
    sortable: true,
    optional: true,
    label: 'Ответственный',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  favorites: {
    label: 'В избранном у пользователей',
    optional: true,
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userAuditors: {
    label: 'Аудиторы',
    optional: true,
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userGroupAuditors: {
    label: 'Группы аудиторов',
    optional: true,
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  parent: {
    optional: true,
    label: 'Главный проект',
    nested: ProjectDtoKeysEnum.PROJECT_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  children: {
    optional: true,
    label: 'Дочерние проекты',
    nested: ProjectDtoKeysEnum.PROJECT_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  files: {
    optional: true,
    label: 'Файлы',
    nested: FileFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  filesCount: {
    optional: true,
    label: 'Количество файлов',
    serializer: yup.number(),
  },
  status: {
    sortable: true,
    label: 'Статус',
    nested: StatusFindDto,
    serializer: (_, nested) => nested,
  },
  finishedAt: {
    label: 'Завершён',
    serializer: yup.date(),
  },
  isFavorite: {
    optional: true,
    label: 'Главный',
    serializer: yup.boolean(),
  },
  hasAccess: {
    optional: true,
    label: 'Есть доступ',
    serializer: yup.boolean(),
  },
  hasDescendants: {
    optional: true,
    label: 'Есть потомки',
    serializer: yup.boolean(),
  },
  checklists: {
    label: 'Чек листы',
    nested: ChecklistFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  masterMilestone: {
    sortable: true,
    optional: true,
    label: 'Мастер-веха',
    nested: MasterMilestoneFindDto,
    serializer: (_, nested) => nested,
  },
  nextTodo: {
    optional: true,
    label: 'Следующая задача',
    nested: ChecklistFindDto,
    serializer: (_, nested) => nested,
  },
  reminder: {
    optional: true,
    label: 'Напоминание',
    nested: NotificationDtoKeysEnum.REMINDER_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  deadlineDelayRequest: {
    label: 'Запрос на перенос дедлайна',
    nested: DeadlineDelayRequestFindDto,
    serializer: (_, nested) => nested,
  },
  workflowPermissions: {
    optional: true,
    label: 'Доступ к задачам',
    nested: ProjectWorkflowPermissionsDto,
    serializer: (_, nested) => nested,
  },
  deals: {
    optional: true,
    label: 'Сделки',
    nested: BusinessDtoKeysEnum.DEAL_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  client: {
    optional: true,
    label: 'Клиент',
    nested: ClientFindDto,
    serializer: (_, nested) => nested,
  },
  direction: {
    optional: true,
    label: 'БС',
    nested: DirectionFindDto,
    serializer: (_, nested) => nested,
  },
  unit: {
    optional: true,
    label: 'Организация',
    nested: UnitFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    optional: true,
    label: 'Филиал',
    nested: FilialFindDto,
    serializer: (_, nested) => nested,
  },
  helpdeskTasks: {
    optional: true,
    label: 'Заявки HD',
    nested: HelpdeskDtoKeyEnum.HELPDESK_TASK_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  subprojectDefaults: {
    optional: true,
    label: 'Параметры по-умолчанию для подзадач',
    nested: SubprojectDefaultsFindDto,
    serializer: (_, nested) => nested,
  },
  thread: {
    optional: true,
    label: 'thread',
    nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  template: {
    optional: true,
    label: 'Шаблон',
    nested: ProjectDtoKeysEnum.PROJECT_TEMPLATE_FIND_DTO,
    serializer: (_, nested) => nested,
  },
});
